/**
 * BST Blog Nav — client-side tag filter and desktop sort.
 */
(function () {
  'use strict';

  const GRID_SELECTOR = '[data-bst-blog-grid]';
  const GRID_COMPONENT_SELECTOR = 'bst-blog-grid';
  const SCROLL_TARGET_SELECTOR = '.shopify-section.bst-main-blog-section';
  const FEATURED_SCROLL_TARGET_SELECTOR = '.shopify-section.bst-blog-featured-section';
  const ARTICLE_SELECTOR = '[data-bst-blog-article]';
  const EMPTY_SELECTOR = '[data-bst-blog-empty]';
  const TAG_SELECTOR = '[data-bst-blog-nav-tag]';
  const SORT_SELECTOR = '[data-bst-blog-nav-sort]';

  class BstBlogNav extends HTMLElement {
    constructor() {
      super();
      this.grid = null;
      this.items = [];
      this.emptyState = null;
      this.tagButtons = [];
      this.sortSelect = null;
      this.activeTag = 'all';
      this.activeSort = 'featured';
      this.placeholder = null;
      this.stickStart = 0;
      this.isStickyEnabled = false;
      this.headerResizeObserver = null;

      this.onTagClick = this.onTagClick.bind(this);
      this.onSortChange = this.onSortChange.bind(this);
      this.onArticlesChanged = this.onArticlesChanged.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    getBlogGridComponent() {
      return document.querySelector(GRID_COMPONENT_SELECTOR);
    }

    isExpanded() {
      return this.getBlogGridComponent()?.dataset.expanded === 'true';
    }

    getInitialCount() {
      const value = parseInt(this.getBlogGridComponent()?.dataset.initialCount || '5', 10);
      return Number.isNaN(value) ? 5 : value;
    }

    refreshItems() {
      if (!this.grid) return;

      this.items = [...this.grid.querySelectorAll(ARTICLE_SELECTOR)];
      this.emptyState = this.grid.querySelector(EMPTY_SELECTOR);
    }

    connectedCallback() {
      this.isStickyEnabled = this.classList.contains('is-sticky');
      if (this.isStickyEnabled) {
        this.initSticky();
      }

      this.grid = document.querySelector(GRID_SELECTOR);
      if (!this.grid) return;

      this.items = [...this.grid.querySelectorAll(ARTICLE_SELECTOR)];
      this.emptyState = this.grid.querySelector(EMPTY_SELECTOR);
      this.tagButtons = [...this.querySelectorAll(TAG_SELECTOR)];
      this.sortSelect = this.querySelector(SORT_SELECTOR);

      if (!this.items.length) return;

      this.activeTag = this.dataset.activeTag || 'all';
      this.activeSort = this.dataset.activeSort || 'featured';

      const params = new URLSearchParams(window.location.search);
      if (params.has('tag')) {
        this.activeTag = params.get('tag') || 'all';
      }
      if (params.has('sort')) {
        this.activeSort = params.get('sort') || 'featured';
      }

      this.syncControls();
      this.apply();

      this.tagButtons.forEach((button) => {
        button.addEventListener('click', this.onTagClick);
      });

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', this.onSortChange);
      }

      document.addEventListener('bst-blog:articles-changed', this.onArticlesChanged);
    }

    disconnectedCallback() {
      this.tagButtons.forEach((button) => {
        button.removeEventListener('click', this.onTagClick);
      });

      if (this.sortSelect) {
        this.sortSelect.removeEventListener('change', this.onSortChange);
      }

      document.removeEventListener('bst-blog:articles-changed', this.onArticlesChanged);

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.headerResizeObserver?.disconnect();
      this.placeholder?.remove();
      this.placeholder = null;
    }

    initSticky() {
      this.placeholder = document.createElement('div');
      this.placeholder.className = 'bst-blog-nav__placeholder';
      this.placeholder.setAttribute('aria-hidden', 'true');
      this.parentNode?.insertBefore(this.placeholder, this);

      this.observeHeaderHeight();
      this.updateStickyOffset();
      this.measureStickStart();
      this.updateFixedState();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      requestAnimationFrame(() => {
        this.updateStickyOffset();
        this.measureStickStart();
        this.updateFixedState();
      });
    }

    onScroll() {
      this.updateFixedState();
    }

    onResize() {
      this.updateStickyOffset();
      this.measureStickStart();
      this.updateFixedState();
    }

    observeHeaderHeight() {
      if (!('ResizeObserver' in window)) return;

      const observeTargets = [
        document.querySelector('.bst-header'),
        document.querySelector('.bst-header__bar'),
      ].filter(Boolean);

      if (!observeTargets.length) return;

      this.headerResizeObserver = new ResizeObserver(() => {
        this.updateStickyOffset();
        if (!this.classList.contains('is-fixed')) {
          this.measureStickStart();
        }
        this.updateFixedState();
      });

      observeTargets.forEach((target) => this.headerResizeObserver.observe(target));
    }

    measureStickStart() {
      if (!this.isStickyEnabled || this.classList.contains('is-fixed')) return;
      this.stickStart = this.getBoundingClientRect().top + window.scrollY;
    }

    updateFixedState() {
      if (!this.isStickyEnabled || !this.placeholder) return;

      const headerOffset = this.getScrollOffset();
      const shouldFix = window.scrollY >= this.stickStart - headerOffset;

      if (shouldFix === this.classList.contains('is-fixed')) {
        if (shouldFix) {
          this.placeholder.style.height = `${this.offsetHeight}px`;
        }
        return;
      }

      this.classList.toggle('is-fixed', shouldFix);
      this.placeholder.style.height = shouldFix ? `${this.offsetHeight}px` : '0';

      if (!shouldFix) {
        requestAnimationFrame(() => this.measureStickStart());
      }
    }

    getScrollOffset() {
      const stickyTop = parseFloat(getComputedStyle(this).getPropertyValue('--bst-blog-nav-sticky-top'));
      if (Number.isFinite(stickyTop) && stickyTop > 0) {
        return stickyTop;
      }

      return this.getHeaderOffset();
    }

    updateStickyOffset() {
      const offset = this.getHeaderOffset();
      const navHeight = this.offsetHeight || 0;
      const offsetValue = `${offset}px`;
      const scrollMargin = offset + navHeight + 16;

      document.documentElement.style.setProperty('--bst-header-height', offsetValue);
      document.documentElement.style.setProperty('--bst-blog-nav-sticky-top', offsetValue);
      document.documentElement.style.setProperty('--bst-blog-nav-scroll-margin', `${scrollMargin}px`);
      this.style.setProperty('--bst-blog-nav-sticky-top', offsetValue);
    }

    getHeaderOffset() {
      const header = document.querySelector('.bst-header');
      if (header) {
        const rect = header.getBoundingClientRect();
        if (rect.height > 0) {
          return Math.ceil(rect.height);
        }
      }

      const headerBar = document.querySelector('.bst-header__bar');
      if (headerBar) {
        const barHeight = Math.ceil(headerBar.getBoundingClientRect().height);
        if (barHeight > 0) {
          return barHeight;
        }
      }

      return this.getFallbackHeaderOffset();
    }

    getFallbackHeaderOffset() {
      return window.matchMedia('(min-width: 990px)').matches ? 72 : 50;
    }

    onArticlesChanged() {
      this.refreshItems();
      this.apply();
    }

    onTagClick(event) {
      const tag = event.currentTarget.dataset.bstBlogNavTag;
      if (!tag || tag === this.activeTag) return;

      this.activeTag = tag;
      this.syncControls();
      this.apply({ scroll: 'blog' });
    }

    onSortChange(event) {
      this.activeSort = event.currentTarget.value || 'featured';
      const scrollTarget = this.activeSort === 'featured' ? 'featured' : 'blog';
      this.apply({ scroll: scrollTarget });
    }

    syncControls() {
      this.tagButtons.forEach((button) => {
        const isActive = button.dataset.bstBlogNavTag === this.activeTag;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (this.sortSelect && this.sortSelect.value !== this.activeSort) {
        this.sortSelect.value = this.activeSort;
      }
    }

    apply(options = {}) {
      this.refreshItems();

      const visibleItems = this.items.filter((item) => this.matchesTag(item));
      const sortedItems = this.sortItems(visibleItems);
      const isExpanded = this.isExpanded();
      const initialCount = this.getInitialCount();

      sortedItems.forEach((item, index) => {
        item.hidden = !isExpanded && index >= initialCount;
        this.grid.appendChild(item);
      });

      this.items
        .filter((item) => !sortedItems.includes(item))
        .forEach((item) => {
          item.hidden = true;
        });

      if (this.emptyState) {
        const visibleCount = isExpanded
          ? sortedItems.length
          : Math.min(sortedItems.length, initialCount);
        this.emptyState.hidden = visibleCount > 0;
      }

      this.updateUrl();

      if (options.scroll) {
        this.scrollToTarget(options.scroll);
      }
    }

    scrollToTarget(targetType = 'blog') {
      const selector =
        targetType === 'featured' ? FEATURED_SCROLL_TARGET_SELECTOR : SCROLL_TARGET_SELECTOR;
      let target = document.querySelector(selector);

      if (!target && targetType === 'featured') {
        target = document.querySelector(SCROLL_TARGET_SELECTOR);
      }

      if (!target) return;

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      target.scrollIntoView({ behavior, block: 'start' });
    }

    matchesTag(item) {
      if (this.activeTag === 'all') return true;

      const tags = (item.dataset.bstBlogTags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);

      return tags.includes(this.activeTag);
    }

    sortItems(items) {
      const sorted = [...items];

      sorted.sort((a, b) => {
        switch (this.activeSort) {
          case 'newest':
            return this.getPublishedAt(b) - this.getPublishedAt(a);
          case 'oldest':
            return this.getPublishedAt(a) - this.getPublishedAt(b);
          case 'title-asc':
            return this.getTitle(a).localeCompare(this.getTitle(b), undefined, { sensitivity: 'base' });
          case 'title-desc':
            return this.getTitle(b).localeCompare(this.getTitle(a), undefined, { sensitivity: 'base' });
          case 'featured':
          default:
            return this.getOrder(a) - this.getOrder(b);
        }
      });

      return sorted;
    }

    getPublishedAt(item) {
      return parseInt(item.dataset.bstBlogPublished || '0', 10);
    }

    getTitle(item) {
      return item.dataset.bstBlogTitle || '';
    }

    getOrder(item) {
      return parseInt(item.dataset.bstBlogOrder || '0', 10);
    }

    updateUrl() {
      const url = new URL(window.location.href);

      if (this.activeTag === 'all') {
        url.searchParams.delete('tag');
      } else {
        url.searchParams.set('tag', this.activeTag);
      }

      if (this.activeSort === 'featured') {
        url.searchParams.delete('sort');
      } else {
        url.searchParams.set('sort', this.activeSort);
      }

      window.history.replaceState({}, '', url);
    }
  }

  if (!customElements.get('bst-blog-nav')) {
    customElements.define('bst-blog-nav', BstBlogNav);
  }
})();
